<template>
  <div class="ai-agent">
    <div class="ai-header">
      <div class="ai-title">AI Energy Manager</div>
      <div class="ai-subtitle">Ask about pricing, units, and trade ideas. I can save your energy bills and optimise trades.</div>
    </div>
    <div ref="messagesContainer" class="ai-messages">
      <div
        v-for="message in messages"
        :key="message.id"
        :class="[
          'ai-message-row',
          message.sender === 'user' ? 'ai-message-user' : 'ai-message-agent',
        ]"
      >
        <div
          :class="[
            'ai-avatar',
            message.sender === 'user' ? 'ai-avatar-user' : 'ai-avatar-agent',
          ]"
        >
          <span>{{ message.sender === "user" ? "U" : "AI" }}</span>
        </div>
        <div
          :class="[
            'ai-bubble',
            message.sender === 'user' ? 'ai-bubble-user' : 'ai-bubble-agent',
          ]"
        >
          <div class="ai-bubble-text" v-html="renderMarkdown(message.text)"></div>
          <span class="ai-bubble-time">{{ message.time }}</span>
        </div>
      </div>
    </div>
    <div class="ai-quick-help">
      <button class="ai-help-button" @click="toggleHelpMenu" aria-label="Quick help">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 3a9 9 0 1 0 9 9 9.01 9.01 0 0 0-9-9Zm.2 13.2a1.1 1.1 0 1 1-1.1 1.1 1.1 1.1 0 0 1 1.1-1.1Zm1.9-5.2-.6.4a1.9 1.9 0 0 0-.9 1.6v.2a1 1 0 0 1-2 0v-.3a3.9 3.9 0 0 1 1.8-3.2l.7-.5a1.6 1.6 0 1 0-2.4-1.4 1 1 0 0 1-2 0 3.6 3.6 0 1 1 5.4 3.2Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <div v-if="showHelpMenu" class="ai-help-menu">
        <div class="ai-help-title">Quick help</div>
        <button
          v-for="item in quickHelpItems"
          :key="item"
          class="ai-help-item"
          @click="useQuickHelp(item)"
        >
          {{ item }}
        </button>
      </div>
    </div>
    <div class="ai-input-row">
      <div v-if="isListening" class="ai-listening-indicator">
        <div class="ai-waves">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <span class="ai-listening-text">Listening…</span>
      </div>
      <input
        v-model="draftMessage"
        type="text"
        placeholder="Type your message..."
        @keydown.enter.prevent="sendMessage"
      />
      <button
        class="ai-mic-button"
        :disabled="isLoading"
        @click="toggleVoiceInput"
        :title="isListening ? 'Stop listening' : 'Start voice input'"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3z"
            fill="currentColor"
          />
          <path
            d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.93V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.07A7 7 0 0 0 19 11z"
            fill="currentColor"
          />
        </svg>
      </button>
      <button :disabled="!draftMessage.trim() || isLoading" @click="sendMessage">
        Send
      </button>
    </div>
  </div>
</template>

<script>
import { marked } from "marked";
import ChatService from "@/services/chat.service";

export default {
  data() {
    return {
      messages: [],
      draftMessage: "",
      nextId: 1,
      isLoading: false,
      isListening: false,
      speechSupported: false,
      recognition: null,
      pollTimer: null,
      pollInFlight: false,
      messageIdSet: new Set(),
      showHelpMenu: false,
      quickHelpItems: [
        "Tomorrow I’m hosting a small party, please save me money.",
        "Bills are high, make it fit my salary.",
        "I want to earn money. Please make me earn money.",
        "I don’t own a roof. Can i buy solar?",
        "I’m retired, my pension shouldn’t go into electricity.",
        "I don’t understand , just do the right thing.",
        "Bills are too high",
        "shopkeeper: bills down, margins up",
        "Solar laga hai, kaise paise kamao?",
        "charging my e-scooter cheaply",
        "factory: electricity cost eating my profits",
        "Neighbour helps neighbour, how can i buy or sell from neighbor?",
        "Apartment society: everyone benefits. can we share solar?",
      ],
    };
  },
  async mounted() {
    marked.setOptions({ breaks: true });
    this.addAgentMessage(
      "👋⚡ Hi! I am your AI Energy Manager. 🤖\n\nHere is how I can help:\n• 🧾 Place trades quickly and smartly\n• 🤝 Auto buy or sell on your behalf\n• 📊 Analyze consumption and help save on bills\n• 📈 Manage telescopic pricing escalation\n• 💡 Guide you to better trading decisions\n\nTell me what you want to do, and I will take it from here."
    );
    await this.fetchMessages();
    this.startPolling();
    this.speechSupported =
      typeof window !== "undefined" &&
      ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    renderMarkdown(text) {
      return marked.parse(text || "");
    },
    getTimeLabel(timeMs) {
      const now = timeMs ? new Date(timeMs) : new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    formatActor(actor) {
      return actor === "AI" ? "agent" : "user";
    },
    async fetchMessages() {
      const userId = localStorage.getItem("_id");
      if (!userId) return;
      if (this.pollInFlight) return;
      try {
        this.pollInFlight = true;
        const response = await ChatService.listMessages(userId);
        const fetched = [];
        (response.messages || []).forEach((msg) => {
          const idKey =
            msg._id || `${msg.actor}-${msg.createdAt}-${msg.message}`;
          if (this.messageIdSet.has(idKey)) return;
          const formattedTime = this.getTimeLabel(msg.createdAt);
          const sender = this.formatActor(msg.actor);
          const text = msg.message || "";
          const alreadyShown = this.messages.some(
            (item) =>
              item.sender === sender &&
              item.text === text &&
              item.time === formattedTime
          );
          if (alreadyShown) return;
          this.messageIdSet.add(idKey);
          fetched.push({
            id: msg._id || this.nextId++,
            sender,
            text,
            time: formattedTime,
          });
        });
        if (fetched.length) {
          this.messages = [...this.messages, ...fetched];
          this.scrollToBottom();
        }
      } catch (error) {
        if (this.messages.length === 1) {
          this.addAgentMessage("⚠️ Unable to load previous messages.");
        }
      } finally {
        this.pollInFlight = false;
      }
    },
    addAgentMessage(text) {
      this.messages.push({
        id: this.nextId++,
        sender: "agent",
        text,
        time: this.getTimeLabel(),
      });
      this.scrollToBottom();
    },
    addUserMessage(text) {
      this.messages.push({
        id: this.nextId++,
        sender: "user",
        text,
        time: this.getTimeLabel(),
      });
      this.scrollToBottom();
    },
    async sendMessage() {
      const trimmed = this.draftMessage.trim();
      if (!trimmed) return;
      const userId = localStorage.getItem("_id");
      if (!userId) {
        this.addAgentMessage("⚠️ Please login to continue.");
        return;
      }
      this.addUserMessage(trimmed);
      this.draftMessage = "";
      try {
        this.isLoading = true;
        const response = await ChatService.postMessage(userId, {
          message: trimmed,
          actor: "USER",
        });
        const msg = response?.message;
        if (msg && msg.actor === "AI") {
          this.messages.push({
            id: msg._id || this.nextId++,
            sender: "agent",
            text: msg.message || "",
            time: this.getTimeLabel(msg.createdAt),
          });
          this.scrollToBottom();
        }
      } catch (error) {
        this.addAgentMessage("⚠️ Failed to send message. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },
    toggleHelpMenu() {
      this.showHelpMenu = !this.showHelpMenu;
    },
    useQuickHelp(item) {
      this.draftMessage = item;
      this.showHelpMenu = false;
      this.sendMessage();
    },
    toggleVoiceInput() {
      if (!this.speechSupported) {
        this.addAgentMessage(
          "⚠️ Voice input is not supported in this browser."
        );
        return;
      }
      if (this.isListening) {
        this.recognition?.stop();
        return;
      }
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      this.recognition = recognition;
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onstart = () => {
        this.isListening = true;
      };
      recognition.onend = () => {
        this.isListening = false;
      };
      recognition.onerror = () => {
        this.isListening = false;
        this.addAgentMessage("⚠️ Voice input failed. Please try again.");
      };
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript || "";
        this.draftMessage = transcript.trim();
        if (this.draftMessage) {
          this.sendMessage();
        }
      };
      recognition.start();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.fetchMessages();
      }, 1000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap");

.ai-agent {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  background: #f6f5ff;
  font-family: "Montserrat", sans-serif;
}

.ai-header {
  padding: 18px 20px;
  background: linear-gradient(135deg, #5b4bb7, #7f6cff);
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 5;
}

.ai-title {
  font-size: 18px;
  font-weight: 700;
}

.ai-subtitle {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 4px;
}

.ai-messages {
  flex: 1;
  padding: 16px 16px 90px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ai-message-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.ai-message-user,
.ai-message-agent {
  justify-content: flex-start;
}

.ai-message-user {
  justify-content: flex-start;
  flex-direction: row-reverse;
}

.ai-bubble {
  max-width: 78%;
  padding: 10px 12px;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  font-size: 13px;
  line-height: 1.4;
}

.ai-bubble-agent {
  background: #ffffff;
  border-top-left-radius: 6px;
}

.ai-bubble-user {
  background: #e8e3ff;
  border-top-right-radius: 6px;
}

.ai-bubble-text {
  margin: 0;
  color: #1f1f1f;
  white-space: normal;
}

.ai-bubble-text :deep(p) {
  margin: 0 0 6px;
}

.ai-bubble-text :deep(p:last-child) {
  margin-bottom: 0;
}

.ai-bubble-text :deep(ul),
.ai-bubble-text :deep(ol) {
  margin: 6px 0;
  padding-left: 18px;
}

.ai-bubble-text :deep(code) {
  background: rgba(91, 75, 183, 0.12);
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 12px;
}

.ai-bubble-text :deep(pre) {
  background: #1f1f1f;
  color: #fff;
  padding: 8px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 12px;
}

.ai-bubble-time {
  display: block;
  margin-top: 6px;
  font-size: 10px;
  color: #8f8f8f;
  text-align: right;
}

.ai-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.ai-avatar-agent {
  background: #5b4bb7;
  color: #ffffff;
}

.ai-avatar-user {
  background: #e0dcff;
  color: #5b4bb7;
}

.ai-input-row {
  position: fixed;
  bottom: calc(52px + env(safe-area-inset-bottom));
  left: 0;
  right: 0;
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #ffffff;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
  align-items: center;
}

.ai-input-row input {
  flex: 1;
  height: 40px;
  border: 1px solid #d8d8d8;
  border-radius: 20px;
  padding: 0 14px;
  font-size: 13px;
}

.ai-input-row button {
  height: 40px;
  padding: 0 18px;
  border-radius: 20px;
  border: none;
  background: #5b4bb7;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-input-row button:disabled {
  opacity: 0.5;
}

.ai-quick-help {
  position: fixed;
  right: 16px;
  bottom: calc(120px + env(safe-area-inset-bottom));
  z-index: 6;
}

.ai-help-button {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  background: #5b4bb7;
  color: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-help-menu {
  position: absolute;
  right: 0;
  bottom: 56px;
  width: min(360px, 86vw);
  max-height: 360px;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-help-title {
  font-size: 12px;
  font-weight: 700;
  color: #5b4bb7;
  margin-bottom: 4px;
}

.ai-help-item {
  text-align: left;
  border: 1px solid #e5e1ff;
  background: #f7f5ff;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 12px;
  color: #2b2b2b;
  font-family: "Montserrat", sans-serif;
}

.ai-help-button svg {
  width: 26px;
  height: 26px;
}

.ai-mic-button {
  width: 40px;
  padding: 0;
  background: #f0ecff;
  color: #5b4bb7;
}

.ai-mic-button svg {
  width: 18px;
  height: 18px;
}

.ai-listening-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  height: 40px;
  background: #f5f2ff;
  border-radius: 20px;
  border: 1px solid #e2ddff;
}

.ai-listening-text {
  font-size: 11px;
  color: #5b4bb7;
  font-weight: 600;
}

.ai-waves {
  display: flex;
  align-items: center;
  gap: 3px;
}

.ai-waves span {
  display: block;
  width: 3px;
  height: 10px;
  border-radius: 2px;
  background: #5b4bb7;
  animation: ai-wave 1s ease-in-out infinite;
}

.ai-waves span:nth-child(2) {
  animation-delay: 0.2s;
}

.ai-waves span:nth-child(3) {
  animation-delay: 0.4s;
}

.ai-waves span:nth-child(4) {
  animation-delay: 0.6s;
}

@keyframes ai-wave {
  0%,
  100% {
    height: 8px;
    opacity: 0.6;
  }
  50% {
    height: 16px;
    opacity: 1;
  }
}
</style>
